import * as React from 'react'
import { useEffect } from 'react'
import { Container } from 'typescript-ioc/es5'
import { AppService } from '@halo/services/app.service'
import { Form, Input, Select } from 'antd'
import { Drawer } from '@pushly/aqe/lib/components'
import { CatalogFieldMapping } from '@pushly/models/lib/structs/catalogs/catalog-field-mapping'
import { CatalogExpectedType } from '@pushly/models/lib/enums/catalogs/catalog-expected-type'

export interface IEditCatalogFieldMappingDrawerProps {
    params: {
        mapping: Partial<CatalogFieldMapping>
        index?: number
        isRequired?: boolean
    } | null
    availableFields?: string[]
    defaultMappings?: CatalogFieldMapping[]
    onClose: () => void
    onUpdate: (mapping: CatalogFieldMapping, index?: number) => void
}

function EditCatalogFieldMappingDrawer(props: IEditCatalogFieldMappingDrawerProps) {
    const appService = Container.get(AppService)
    const [form] = Form.useForm<CatalogFieldMapping>()

    useEffect(() => {
        if (props.params) {
            form.setFieldsValue({
                catalog_field: undefined,
                json_path: undefined,
                expected_type: undefined,
                ...props.params.mapping,
            })
        }
    }, [props.params])

    const isEditMode = props.params?.index != null
    const title = isEditMode ? 'Edit Field Mapping' : 'Add Field Mapping'
    const submitText = isEditMode ? 'Update Field Mapping' : 'Add Field Mapping'
    const availableFields = props.availableFields ?? []
    const allFieldsMapped = !isEditMode && availableFields.length === 0

    function handleFieldSelected(catalogField: string) {
        form.setFieldsValue({ catalog_field: catalogField })
        const defaultMapping = (props.defaultMappings ?? []).find((m) => m.catalog_field === catalogField)
        if (defaultMapping) {
            form.setFieldsValue({
                json_path: defaultMapping.json_path,
                expected_type: defaultMapping.expected_type ?? undefined,
            })
        }
    }

    return (
        <Drawer
            width={800}
            getContainer={appService.getAppContainer}
            className="edit-catalog-field-mapping-drawer"
            title={title}
            placement="right"
            closable={true}
            submitText={submitText}
            onSubmit={() => {
                form.submit()
            }}
            onClose={props.onClose}
            open={!!props.params}
        >
            {props.params && (
                <Form
                    layout="horizontal"
                    form={form}
                    onFinish={() => {
                        props.onUpdate(
                            form.getFieldsValue() as CatalogFieldMapping,
                            props.params?.index,
                        )
                    }}
                    size="small"
                    labelAlign="left"
                    requiredMark={false}
                >
                    <Form.Item
                        label="Catalog Field"
                        name="catalog_field"
                        rules={[{ required: true, message: 'Catalog Field is required' }]}
                    >
                        {isEditMode ? (
                            <Input autoComplete="off" disabled={!!props.params?.isRequired} />
                        ) : (
                            <Select
                                placeholder={allFieldsMapped ? 'All fields mapped' : 'Select catalog field'}
                                disabled={allFieldsMapped}
                                showSearch
                                onChange={handleFieldSelected}
                            >
                                {availableFields.map((field) => (
                                    <Select.Option key={field} value={field}>
                                        {field}
                                    </Select.Option>
                                ))}
                            </Select>
                        )}
                    </Form.Item>

                    <Form.Item
                        label="JSONPath"
                        name="json_path"
                        rules={[{ required: true, message: 'JSONPath is required' }]}
                    >
                        <Input autoComplete="off" />
                    </Form.Item>

                    <Form.Item label="Expected Type" name="expected_type">
                        <Select allowClear placeholder="Select expected type">
                            {Object.values(CatalogExpectedType).map((type) => (
                                <Select.Option key={type} value={type}>
                                    {type}
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>
                </Form>
            )}
        </Drawer>
    )
}

export default EditCatalogFieldMappingDrawer
